"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createMessageApiService, subscribeToConversationChanges, type ConversationSummary } from '@gorilla-works/messages'

import { useAuth } from '@/contexts/auth-context'
import { useWorkflowApi } from '@/hooks/use-workflow-api'
import { env } from '@/lib/env'

// One conversation the caller has access to. The row is built in three phases so the inbox scales to a
// Program Manager with thousands of threads while still filtering the whole set:
//   1) INDEX — my-states per entity type gives every conversation's id + combined unread + muted +
//      importance (minimal, no server paging up to a 10k ceiling). Renders instantly.
//   2) SUMMARIES — conversations/summaries for EVERY id (chunked, in the background) adds the preview,
//      last-activity and the client/internal split. These are light counter reads, so fetching them for
//      the whole set is cheap — and it lets the client/internal filter, recency sort and previews cover
//      every conversation, not just the visible page.
//   3) SEARCH — worker/WO#/stage/dates (a wide index search, the one genuinely heavy call). Only the
//      visible page by default, so a plain open/refresh stays O(page). When the caller needs metadata for
//      the WHOLE set (`needAllMetadata` — the Filters popover is open, or a stage/client filter or a search is
//      active), every row is also searched in the background in chunks of SEARCH_ID_CHUNK.
// Covers Assignment (WorkOrder) plus every request family; the redirect reuses the worker-search rules.
export interface InboxConversation {
  entityTypeId: string
  entityId: string
  /** `${entityTypeId}:${entityId}` — unique across types (a request id can collide with an Assignment id). */
  id: string
  /** Redirect handoff — request rows route through `onNavigateToRequestTab(documentType, row)`; Assignment
   *  rows jump in-grid off Stage/WorkerFullName/ClientName/FboWorkOrderNumber. See use-workflow-row-navigation. */
  isRequestRow: boolean
  documentType: string
  requestId: string
  workerFullName: string
  clientName: string
  fboWorkOrderNumber: string
  stage: string
  startDate: string | null
  endDate: string | null
  unread: number
  internalUnread: number
  clientUnread: number
  hasImportant: boolean
  isMuted: boolean
  messageCount: number
  previewSnippet: string
  previewAuthor: string
  lastActivity: string | null
  /** False until enrichment (page-level or the on-demand background pass) has filled in metadata + preview + the unread split. */
  enriched: boolean
}

interface ConversationState {
  entityId: string
  messageCount: number
  unreadCount: number
  hasImportantUnread: boolean
  isSubscribed: boolean
}

// The message entity types that carry conversations, and how each joins to its search-index metadata.
//   - Assignment       → WorkOrder index by FboAssignmentId
//   - Vms/Ats/Client   → requests index by RequestId (the conversation id IS the request's doc id)
//   - Request/Onboard. → requests index by PreVerificationRequestId (the unified index moved RequestId
//                        onto the FBO doc id, so onboarding threads — keyed on the portal id — join here)
// `documentType` is what the redirect hands to onNavigateToRequestTab (which tab to open).
interface EntityTypeConfig {
  entityTypeId: string
  source: 'wo' | 'requests'
  joinField: 'fboAssignmentId' | 'requestId' | 'preVerificationRequestId'
  filterField: string
  isRequest: boolean
}

const ENTITY_TYPES: EntityTypeConfig[] = [
  { entityTypeId: 'Assignment', source: 'wo', joinField: 'fboAssignmentId', filterField: 'FboAssignmentId', isRequest: false },
  { entityTypeId: 'VmsRequest', source: 'requests', joinField: 'requestId', filterField: 'RequestId', isRequest: true },
  { entityTypeId: 'AtsPlacementRequest', source: 'requests', joinField: 'requestId', filterField: 'RequestId', isRequest: true },
  { entityTypeId: 'ClientRequest', source: 'requests', joinField: 'requestId', filterField: 'RequestId', isRequest: true },
  { entityTypeId: 'Request', source: 'requests', joinField: 'preVerificationRequestId', filterField: 'PreVerificationRequestId', isRequest: true },
  { entityTypeId: 'OnboardingRequest', source: 'requests', joinField: 'preVerificationRequestId', filterField: 'PreVerificationRequestId', isRequest: true },
]

const CONFIG_BY_TYPE = new Map(ENTITY_TYPES.map((c) => [c.entityTypeId, c]))

const WO_SELECT = 'FboAssignmentId,Stage,WorkerFullName,ClientName,FboWorkOrderNumber,StartDate,EndDate'
const REQUEST_SELECT = 'RequestId,PreVerificationRequestId,DocumentType,WorkerFullName,Client/Name,FboWorkOrderId,AtsPlacementId,VmsExternalRequestId,StartDate,EndDate,Status'

// Family label shown as the row's "stage" for a request row (Assignment rows show their real stage).
const REQUEST_FAMILY_LABEL: Record<string, string> = {
  AtsPlacementRequest: 'ATS',
  VmsRequest: 'VMS',
  ClientRequest: 'Client Request',
  Request: 'Onboarding',
  OnboardingRequest: 'Onboarding',
}

const key = (entityTypeId: string, entityId: string) => `${entityTypeId}:${entityId}`
const quote = (v: string) => `'${v.replace(/'/g, "''")}'`

// Summaries fan-out: the endpoint takes at most this many ids per call, so a large index is chunked.
const SUMMARIES_CHUNK = 200
// Worker-name search runs WITHIN the loaded conversations (chunked), so it never truncates matches. Each
// chunk's OData id-filter is combined with the name predicate; this bounds the fan-out to the id-filter size.
// enrich() chunks its metadata searches by the same size.
const SEARCH_ID_CHUNK = 200
// Bounded reconcile after a conversation changes elsewhere (e.g. read/mute inside the still-open chat).
// The installed 1.0.14 change signal carries no settled count and my-states/summaries lag the just-committed
// write, so re-read a few times, staggered, until it converges — mirrors useStageMessageCounts.
const RECONCILE_DELAYS_MS = [1500, 8000, 20000]

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = []
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size))
  return out
}

// Fold a conversation summary (preview + last-activity + client/internal split + settled counts + mute)
// onto an index row. Used by the background summaries pass AND the change-reconcile, so both stay in step.
function mergeSummary(r: InboxConversation, s: ConversationSummary): InboxConversation {
  return {
    ...r,
    unread: s.unreadCount ?? r.unread,
    internalUnread: s.internalUnreadCount ?? 0,
    clientUnread: s.clientUnreadCount ?? 0,
    hasImportant: s.hasImportantUnread ?? r.hasImportant,
    // Direct, not "only when false" — a summary fetched after an UNMUTE must clear the muted flag too.
    isMuted: s.isSubscribed === false,
    messageCount: s.messageCount ?? r.messageCount,
    previewSnippet: String(s.preview?.snippet ?? ''),
    previewAuthor: String(s.preview?.authorName ?? ''),
    lastActivity: s.preview?.createdAt ?? null,
  }
}

/**
 * The caller's own conversations for the messages inbox, across Assignment + every request family.
 * Fetches the lightweight index while `active`, enriches on demand via `enrich(pageRows)`, and — while
 * `needAllMetadata` — enriches every row in the background.
 */
export function useInboxConversations(active: boolean, needAllMetadata = false) {
  const { token } = useAuth()
  const { searchWorkflowItems } = useWorkflowApi()

  const searchRef = useRef(searchWorkflowItems)
  searchRef.current = searchWorkflowItems
  const tokenRef = useRef(token)
  tokenRef.current = token
  const requestIdRef = useRef(0)
  // Enrichment in-flight guard ONLY (dedup of "already enriched" is the row's own `enriched` flag, which
  // load() resets by replacing the rows — a persistent id-set here would survive a reload and, if the
  // enrich effect fired against the stale rows first, mark the fresh rows as done and skip enriching them).
  const enrichInFlightRef = useRef<Set<string>>(new Set())

  const [rows, setRows] = useState<InboxConversation[]>([])
  const [isLoading, setIsLoading] = useState(false)
  // Bumped each time load() installs a fresh index, so the on-demand background pass re-runs for the new rows.
  const [loadVersion, setLoadVersion] = useState(0)

  // Live rows, so searchCandidateIds can scope its server search to the loaded conversations without
  // being recreated on every change.
  const rowsRef = useRef(rows)
  rowsRef.current = rows
  // Which known conversations changed and still need reconciling, and the staggered reconcile timers.
  const pendingReconcileRef = useRef<Map<string, Set<string>>>(new Map())
  const reconcileTimersRef = useRef<ReturnType<typeof setTimeout>[]>([])

  const load = useCallback(async () => {
    const authToken = tokenRef.current
    if (!authToken) return

    const currentRequestId = ++requestIdRef.current
    enrichInFlightRef.current = new Set()
    setIsLoading(true)
    try {
      // INDEX: my-states per entity type, in parallel. Each returns every conversation of that type the
      // caller is attached to — id + combined unread + muted + importance — with no metadata.
      const perType = await Promise.all(
        ENTITY_TYPES.map(async (cfg) => {
          try {
            const res = await fetch(
              `${env.messagesApiUrl}/conversations/my-states?entityTypeId=${encodeURIComponent(cfg.entityTypeId)}`,
              { headers: { Authorization: `Bearer ${authToken}` } }
            )
            if (!res.ok) return { cfg, states: [] as ConversationState[] }
            return { cfg, states: (await res.json()) as ConversationState[] }
          } catch {
            return { cfg, states: [] as ConversationState[] }
          }
        })
      )
      if (currentRequestId !== requestIdRef.current) return

      // A reload carries each row's previous display values (metadata + preview) over, so persisted filters
      // don't exclude every row — flashing the list to empty — until summaries + enrich land again. Only a
      // display fallback: enriched stays false so the row is still re-fetched and overwritten, and the
      // counts always come from the fresh index.
      const previous = new Map(rowsRef.current.map((r) => [r.id, r]))
      const index: InboxConversation[] = []
      for (const { cfg, states } of perType) {
        for (const state of states) {
          const entityId = String(state.entityId)
          if (!entityId) continue
          // Drop conversations the caller is attached to but that carry no messages yet — a
          // participant-only shell (access seeded before anyone posted) is noise in the inbox. The
          // count is authoritative from the index, so this needs no summaries round trip.
          if (!(state.messageCount > 0)) continue
          const id = key(cfg.entityTypeId, entityId)
          const prev = previous.get(id)
          // The carried client/internal split only while still unread — after a bulk "Mark read" it would
          // otherwise hold the Client/Internal chip counts above 0 until summaries land.
          const carrySplit = state.unreadCount > 0
          index.push({
            entityTypeId: cfg.entityTypeId,
            entityId,
            id,
            isRequestRow: cfg.isRequest,
            documentType: prev?.documentType ?? '',
            requestId: prev?.requestId ?? '',
            workerFullName: prev?.workerFullName ?? '',
            clientName: prev?.clientName ?? '',
            fboWorkOrderNumber: prev?.fboWorkOrderNumber ?? '',
            stage: cfg.isRequest ? (REQUEST_FAMILY_LABEL[cfg.entityTypeId] ?? 'Request') : (prev?.stage ?? ''),
            startDate: prev?.startDate ?? null,
            endDate: prev?.endDate ?? null,
            unread: state.unreadCount,
            internalUnread: carrySplit ? (prev?.internalUnread ?? 0) : 0,
            clientUnread: carrySplit ? (prev?.clientUnread ?? 0) : 0,
            hasImportant: state.hasImportantUnread,
            isMuted: state.isSubscribed === false,
            messageCount: state.messageCount,
            previewSnippet: prev?.previewSnippet ?? '',
            previewAuthor: prev?.previewAuthor ?? '',
            lastActivity: prev?.lastActivity ?? null,
            enriched: false,
          })
        }
      }

      setRows(index)
      setLoadVersion((v) => v + 1)
      setIsLoading(false)

      // SUMMARIES (background): preview + last-activity + client/internal split for EVERY conversation,
      // chunked per type. Cheap counter reads — fetching them for the whole set is what lets the
      // client/internal filter and the recency sort work across all rows, not only the visible page.
      const api = createMessageApiService({ apiEndpoint: env.messagesApiUrl, userToken: () => authToken })
      const summaryByKey = new Map<string, ConversationSummary>()
      await Promise.all(
        ENTITY_TYPES.flatMap((cfg) => {
          const ids = index.filter((r) => r.entityTypeId === cfg.entityTypeId).map((r) => r.entityId)
          return chunk(ids, SUMMARIES_CHUNK).map((c) =>
            api
              .fetchConversationSummaries({ entityTypeId: cfg.entityTypeId, entityIds: c })
              .then((list) => { for (const s of list) summaryByKey.set(key(cfg.entityTypeId, String(s.entityId)), s) })
              .catch(() => {})
          )
        })
      )
      if (currentRequestId !== requestIdRef.current) return
      setRows((prev) => prev.map((r) => { const s = summaryByKey.get(r.id); return s ? mergeSummary(r, s) : r }))
    } catch {
      // Best-effort — the inbox surfaces its own empty/error state.
    } finally {
      if (currentRequestId === requestIdRef.current) setIsLoading(false)
    }
  }, [])

  // SEARCH (the visible page, and each chunk of the on-demand background pass): the worker/WO#/stage/dates metadata —
  // the one wide, heavy call. Groups by entity type: one search per type present, chunked by SEARCH_ID_CHUNK.
  // Preview/split/recency already came from the background summaries pass, so this must NOT touch those fields.
  const enrich = useCallback(async (pageRows: InboxConversation[]) => {
    const authToken = tokenRef.current
    if (!authToken) return

    // The load generation this enrich belongs to. A reload (load()) bumps requestIdRef and installs fresh
    // rows; if it happens while this enrich is in flight, the metadata we fetched is now stale — so on
    // resolve we must NOT stamp the fresh rows (which share the same stable ids) with it, or a row whose
    // data changed in the gap (e.g. stage advanced) keeps the old values and is never re-enriched.
    const gen = requestIdRef.current

    // Dedup on the row's own `enriched` flag — NOT a persistent id-set — so a reload (which replaces the
    // rows with fresh enriched=false ones) always re-enriches; the in-flight guard only prevents a second
    // fetch of the same page while its first is still running.
    const pending = pageRows.filter((r) => !r.enriched && !enrichInFlightRef.current.has(r.id))
    if (pending.length === 0) return
    pending.forEach((r) => enrichInFlightRef.current.add(r.id))

    try {
      const byType = new Map<string, InboxConversation[]>()
      for (const r of pending) {
        const list = byType.get(r.entityTypeId)
        if (list) list.push(r)
        else byType.set(r.entityTypeId, [r])
      }

      const metaByKey = new Map<string, Record<string, any>>()

      await Promise.all(
        Array.from(byType.entries()).flatMap(([entityTypeId, group]) => {
          const cfg = CONFIG_BY_TYPE.get(entityTypeId)
          if (!cfg) return []
          return chunk(group.map((r) => r.entityId), SEARCH_ID_CHUNK).map(async (ids) => {
            try {
              const clauses = ids.map((id) => `${cfg.filterField} eq ${cfg.source === 'wo' ? id : quote(id)}`).join(' or ')
              const result =
                cfg.source === 'wo'
                  ? await searchRef.current(undefined, undefined, [], 1, ids.length, '*', WO_SELECT, `DocumentType eq 'Assignment' and (${clauses})`)
                  // Explicit orderBy: the inbox is under the root WorkOrder provider, so an omitted orderBy
                  // would default to CreatedDateTime desc — a field the shared requests index lacks (400).
                  // RequestDate is the index's sort field (mirrors worker search's combined 'Requests' call).
                  : await searchRef.current(undefined, undefined, [], 1, ids.length, '*', REQUEST_SELECT, `(${clauses})`, 'RequestDate desc', undefined, 'Requests')
              for (const row of (result as any).data ?? []) {
                const joinValue = String((row as any)[cfg.joinField] ?? '')
                if (joinValue) metaByKey.set(key(entityTypeId, joinValue), row as Record<string, any>)
              }
            } catch {
              /* leave these rows with index-only info */
            }
          })
        })
      )

      const pendingIds = new Set(pending.map((p) => p.id))
      setRows((prev) => {
        // A reload happened while this fetch was in flight — the metadata is stale; drop it and let the
        // fresh rows (enriched=false) be re-enriched by the reload's own enrich pass.
        if (gen !== requestIdRef.current) return prev
        return prev.map((r) => {
          if (!pendingIds.has(r.id)) return r
          // Mark enriched even on a search miss (meta = {}): the record simply isn't in the index, so a
          // retry wouldn't help and would loop the effect. It shows as an id until the next reload.
          const meta = metaByKey.get(r.id) ?? {}
          const clientName = String(meta.clientName ?? meta.client?.name ?? meta.client?.Name ?? '')
          const woNumber = r.isRequestRow
            ? String(
                meta.documentType === 'AtsPlacementRequest'
                  ? (meta.atsPlacementId ?? '')
                  : (meta.vmsExternalRequestId ?? meta.fboWorkOrderId ?? '')
              )
            : String(meta.fboWorkOrderNumber ?? meta.workOrderNumber ?? '')
          return {
            ...r,
            documentType: r.isRequestRow ? String(meta.documentType ?? r.entityTypeId) : '',
            requestId: r.isRequestRow ? String(meta.requestId ?? '') : '',
            workerFullName: String(meta.workerFullName ?? ''),
            clientName,
            fboWorkOrderNumber: woNumber,
            stage: r.isRequestRow ? r.stage : String(meta.stage ?? ''),
            startDate: (meta.startDate as string) ?? null,
            endDate: (meta.endDate as string) ?? null,
            enriched: true,
          }
        })
      })
    } finally {
      // Only this generation manages the current in-flight set; after a reload it's a fresh set the new
      // enrich pass owns, so a stale enrich must not delete from it.
      if (gen === requestIdRef.current) pending.forEach((r) => enrichInFlightRef.current.delete(r.id))
    }
  }, [])

  // On-demand background metadata for EVERY row (stage / client / WO #), only while the caller needs it, so a
  // plain open/refresh doesn't search the whole set. Re-runs for each freshly loaded index (loadVersion). One
  // enrich per type-chunk, NOT awaited as a whole: each chunk applies and leaves the in-flight set as soon as it
  // resolves, so the page-level enrich isn't starved (a single enrich(allRows) would claim every row up front).
  // Rows already enriched or in flight are skipped by enrich itself, so re-running this is cheap.
  useEffect(() => {
    if (!active || !needAllMetadata) return
    const pending = rowsRef.current.filter((r) => !r.enriched)
    for (const cfg of ENTITY_TYPES) {
      for (const c of chunk(pending.filter((r) => r.entityTypeId === cfg.entityTypeId), SEARCH_ID_CHUNK)) void enrich(c)
    }
  }, [active, needAllMetadata, loadVersion, enrich])

  // Worker-name search. Runs WITHIN the loaded conversations, grouped by type and chunked, combining each
  // chunk's id-filter with the name predicate. A plain global name search capped at a top-N could let newer
  // unrelated records fill every slot and hide an older loaded conversation; scoping to the loaded ids means
  // every one is tested against the name and none can be truncated away. Returns the set of matching entity-id
  // VALUES for the caller to intersect with its rows; null for an empty term ("no restriction").
  const searchCandidateIds = useCallback(async (term: string): Promise<Set<string> | null> => {
    const q = term.trim()
    if (!q) return null
    const authToken = tokenRef.current
    if (!authToken) return new Set()

    // Prefix-match each term ("will" → "will*") so partial names match as the user types; searchMode 'all'
    // narrows as more terms are added. Strip Lucene-significant characters and lowercase (wildcard queries
    // aren't analyzed, so they must match the lowercased index tokens).
    const terms = q
      .split(/\s+/)
      .map((tkn) => tkn.replace(/[^\p{L}\p{N}._-]/gu, '').toLowerCase())
      .filter((tkn) => tkn.length >= 1)
      .map((tkn) => `${tkn}*`)
      .join(' ')
    if (!terms) return new Set()
    const namePredicate = `search.ismatch('${terms}', 'WorkerFullName', 'full', 'all')`

    const idsByType = new Map<string, string[]>()
    for (const r of rowsRef.current) {
      const list = idsByType.get(r.entityTypeId)
      if (list) list.push(r.entityId)
      else idsByType.set(r.entityTypeId, [r.entityId])
    }

    const matched = new Set<string>()
    await Promise.all(
      Array.from(idsByType.entries()).flatMap(([entityTypeId, ids]) => {
        const cfg = CONFIG_BY_TYPE.get(entityTypeId)
        if (!cfg) return []
        return chunk(ids, SEARCH_ID_CHUNK).map(async (c) => {
          try {
            const idClause = c.map((id) => `${cfg.filterField} eq ${cfg.source === 'wo' ? id : quote(id)}`).join(' or ')
            const result =
              cfg.source === 'wo'
                ? await searchRef.current(undefined, undefined, [], 1, c.length, '*', cfg.filterField, `DocumentType eq 'Assignment' and (${idClause}) and ${namePredicate}`)
                : await searchRef.current(undefined, undefined, [], 1, c.length, '*', 'RequestId,PreVerificationRequestId', `(${idClause}) and ${namePredicate}`, 'RequestDate desc', undefined, 'Requests')
            for (const row of (result as any).data ?? []) {
              const v = String((row as any)[cfg.joinField] ?? '')
              if (v) matched.add(v)
            }
          } catch {
            /* skip this chunk — a partial name filter is better than dropping the whole search */
          }
        })
      })
    )
    return matched
  }, [])

  // Re-read the changed conversations' summaries and fold them onto their rows — a targeted refresh bounded
  // to the ids that actually changed, not a full reload. `clearAfter` empties the pending set on the final
  // staggered pass.
  const reconcilePending = useCallback(async (clearAfter: boolean) => {
    const authToken = tokenRef.current
    if (!authToken) return
    const snapshot = Array.from(pendingReconcileRef.current.entries()).map(([t, s]) => [t, Array.from(s)] as const)
    if (clearAfter) pendingReconcileRef.current = new Map()
    if (snapshot.length === 0) return

    const api = createMessageApiService({ apiEndpoint: env.messagesApiUrl, userToken: () => authToken })
    const summaryByKey = new Map<string, ConversationSummary>()
    await Promise.all(
      snapshot.flatMap(([entityTypeId, ids]) =>
        chunk(ids, SUMMARIES_CHUNK).map((c) =>
          api
            .fetchConversationSummaries({ entityTypeId, entityIds: c })
            .then((list) => { for (const s of list) summaryByKey.set(key(entityTypeId, String(s.entityId)), s) })
            .catch(() => {})
        )
      )
    )
    if (summaryByKey.size === 0) return
    setRows((prev) => prev.map((r) => { const s = summaryByKey.get(r.id); return s ? mergeSummary(r, s) : r }))
  }, [])

  const scheduleReconcile = useCallback(() => {
    for (const t of reconcileTimersRef.current) clearTimeout(t)
    reconcileTimersRef.current = RECONCILE_DELAYS_MS.map((ms, i) =>
      setTimeout(() => void reconcilePending(i === RECONCILE_DELAYS_MS.length - 1), ms)
    )
  }, [reconcilePending])

  // While the inbox is open — and its chat panel may be open beside it — a read/unread/mute done in that
  // panel fires the package's conversation-change signal without reloading the inbox. Reconcile just those
  // conversations so the counts, filters and ordering stay correct, with staggered retries because the
  // installed 1.0.14 signal carries no settled count and my-states/summaries lag the just-committed write.
  useEffect(() => {
    if (!active) return
    // Cast: installed 1.0.14 types the listener as 2-arg (no unreadCount); >= 1.0.15 adds the third arg.
    // Typed here and cast to the package's current listener type, so the settled unread is used when present
    // and this compiles against either version. Drop the cast at the 1.0.15 relock.
    const onConversationChange = (entityTypeId: string, entityId: string, unreadCount?: number) => {
      if (!CONFIG_BY_TYPE.has(entityTypeId)) return
      const id = entityId != null ? String(entityId) : ''
      if (!id) return
      // Fast path when the settled count is carried (>= 1.0.15): apply it now for snappy feedback; the
      // reconcile below still refreshes the split/preview/mute.
      if (unreadCount != null) {
        setRows((prev) =>
          prev.map((r) =>
            r.id === key(entityTypeId, id) ? { ...r, unread: unreadCount, hasImportant: unreadCount > 0 ? r.hasImportant : false } : r
          )
        )
      }
      const set = pendingReconcileRef.current.get(entityTypeId)
      if (set) set.add(id)
      else pendingReconcileRef.current.set(entityTypeId, new Set([id]))
      scheduleReconcile()
    }
    const unsubscribe = subscribeToConversationChanges(
      onConversationChange as Parameters<typeof subscribeToConversationChanges>[0]
    )
    return () => {
      unsubscribe()
      for (const t of reconcileTimersRef.current) clearTimeout(t)
      reconcileTimersRef.current = []
      pendingReconcileRef.current = new Map()
    }
  }, [active, scheduleReconcile])

  useEffect(() => {
    if (active) void load()
  }, [active, load])

  // True once every loaded row has been enriched (and for an empty set) — until then the stage, client and
  // WO # filters may still grow.
  const metadataComplete = useMemo(() => rows.every((r) => r.enriched), [rows])

  return { rows, isLoading, enrich, searchCandidateIds, refresh: load, metadataComplete }
}
